import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Sparkle, Microphone, PaperPlaneRight, Spinner, Stop, X, Warning, CurrencyDollar } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { StatusBadge } from "@/components/common";
import { toast } from "sonner";

export function AskFlapiBar() {
  const { active } = useStudio();
  const location = useLocation();
  const askLabel = `${active?.name || "Studio"} AI`;
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);
  const sessionRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  // Auto-close the response panel whenever the route changes so it never
  // stays "stuck" on top of a different page.
  useEffect(() => {
    setOpen(false);
    setResult(null);
  }, [location.pathname]);

  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setOpen(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/ask", { text, session_id: sessionRef.current });
      sessionRef.current = data.session_id;
      setResult(data);
    } catch (e) {
      toast.error("Studio AI could not respond. Check AI provider status.");
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const toggleMic = async () => {
    if (recording) {
      mediaRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "command.webm");
        setBusy(true);
        try {
          const { data } = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setText(data.text);
          toast.success("Transcribed your voice command");
        } catch {
          toast.error("Speech-to-text failed");
        } finally {
          setBusy(false);
        }
      };
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  return (
    <>
      {open && result && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-[92px] sm:bottom-[100px] z-40 w-[min(680px,94vw)]">
          <div className="glass rounded-2xl p-5 shadow-2xl max-h-[60vh] overflow-y-auto overflow-x-hidden animate-fadeUp">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <Sparkle size={18} weight="fill" className="text-[#00E5FF]" />
                <span className="font-display font-bold text-white">{askLabel} understood</span>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ask-result-close" className="text-zinc-500 hover:text-white shrink-0"><X size={18} /></button>
            </div>
            <p className="mt-3 text-sm text-zinc-200 break-words" data-testid="ask-reply">{result.reply}</p>
            {result.preview_actions?.length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Preview Actions</div>
                <ul className="space-y-1.5">
                  {result.preview_actions.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#00E5FF] shrink-0" /><span className="min-w-0 break-words">{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-zinc-500">Intent: <span className="font-mono text-zinc-300">{result.intent}</span></span>
              {result.estimated_cost_usd > 0 && (
                <span className="inline-flex items-center gap-1 text-xs text-[#FFB800]"><CurrencyDollar size={14} weight="bold" />Est. ${result.estimated_cost_usd}</span>
              )}
              {result.is_destructive_or_expensive && (
                <span className="inline-flex items-center gap-1 text-xs text-[#FF3366]"><Warning size={14} weight="fill" />Needs confirmation</span>
              )}
            </div>
            {result.requires_confirmation && (
              <div className="mt-4 flex gap-2">
                <button data-testid="ask-confirm" onClick={() => { toast.success("Phase 1: generation is planned, not executed. Providers will run this in Phase 2."); setOpen(false); }}
                  className="rounded-full bg-magic px-4 py-2 text-sm font-semibold text-black hover:opacity-90 transition-opacity">Confirm Generation</button>
                <button data-testid="ask-cancel" onClick={() => setOpen(false)}
                  className="rounded-full border border-white/10 bg-[#1C1C1C] px-4 py-2 text-sm text-white hover:border-white/30 transition-colors">Cancel</button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="fixed left-1/2 -translate-x-1/2 bottom-[68px] lg:bottom-6 z-40 w-[min(680px,94vw)]">
        <div className="glass rounded-full pl-5 pr-2 py-2 flex items-center gap-3 shadow-2xl">
          <Sparkle size={20} weight="fill" className="text-[#00E5FF] shrink-0 animate-pulseGlow" />
          <input
            data-testid="ask-flapi-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={`Ask ${askLabel}…  describe an episode, character, edit or translation`}
            className="flex-1 bg-transparent text-sm text-white placeholder:text-zinc-500 outline-none min-w-0"
          />
          <button onClick={toggleMic} data-testid="ask-mic-btn"
            className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center transition-colors ${recording ? "bg-[#FF3366] text-white animate-pulseGlow" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
            {recording ? <Stop size={17} weight="fill" /> : <Microphone size={18} weight="duotone" />}
          </button>
          <button onClick={send} data-testid="ask-send-btn" disabled={busy}
            className="shrink-0 h-9 w-9 rounded-full bg-magic flex items-center justify-center text-black hover:opacity-90 transition-opacity disabled:opacity-50">
            {busy ? <Spinner size={17} className="animate-spin" /> : <PaperPlaneRight size={17} weight="fill" />}
          </button>
        </div>
      </div>
    </>
  );
}
